import React, { useState, useMemo } from "react";
import { components } from "../../../../api/spec";
import GuestSearchBar from "../shared/components/GuestSearchBar";
import PInput from "../../../ui/input/PInput";
import GuestFilterTabs from "../guest-list/components/GuestFilterTabs";
import GuestListItem from "../guest-list/components/GuestListItem";
import PCheckbox from "../../../ui/input/PCheckbox";
import PButton from "../../../ui/input/PButton";

type EventAttendeeSummarySchema =
  components["schemas"]["EventAttendeeSummarySchema"];
type EventAttendeeStatus = components["schemas"]["EventAttendeeStatus"];

interface SelectAttendeesScreenProps {
  attendees: EventAttendeeSummarySchema[];
  onNext: (selectedAttendees: EventAttendeeSummarySchema[]) => void;
  selectedAttendees?: EventAttendeeSummarySchema[] | null;
}

interface FilterCriteria {
  searchQuery: string;
  status: EventAttendeeStatus | null;
  minConnections: number | null;
  maxConnections: number | null;
  minNotifications: number | null;
  maxNotifications: number | null;
}

const SelectAttendeesScreen: React.FC<SelectAttendeesScreenProps> = ({
  attendees,
  onNext,
  selectedAttendees,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    selectedAttendees
      ? new Set(selectedAttendees.map((a) => a.guid!))
      : new Set(),
  );
  const [filters, setFilters] = useState<FilterCriteria>({
    searchQuery: "",
    status: null,
    minConnections: null,
    maxConnections: null,
    minNotifications: null,
    maxNotifications: null,
  });

  const counts = useMemo(
    () =>
      attendees.reduce(
        (acc, attendee) => {
          if (attendee.status) {
            acc[attendee.status] = (acc[attendee.status] || 0) + 1;
          }
          return acc;
        },
        {
          not_signed_up: 0,
          started: 0,
          completed: 0,
          not_started: 0,
          pending_review: 0,
        } as Record<EventAttendeeStatus, number>,
      ),
    [attendees],
  );

  // Filter attendees based on criteria
  const filteredAttendees = useMemo(
    () =>
      attendees.filter((attendee) => {
        // Search filter
        if (filters.searchQuery) {
          const query = filters.searchQuery.toLowerCase();
          const fullName = (attendee.full_name || "").toLowerCase();
          if (!fullName.includes(query)) {
            return false;
          }
        }

        // Status filter
        if (filters.status !== null && attendee.status !== filters.status) {
          return false;
        }

        // Connection count filters
        const connections = attendee.num_connected || 0;
        if (
          filters.minConnections !== null &&
          connections < filters.minConnections
        ) {
          return false;
        }
        if (
          filters.maxConnections !== null &&
          connections > filters.maxConnections
        ) {
          return false;
        }

        // Notification count filters
        const notifications = attendee.num_notified || 0;
        if (
          filters.minNotifications !== null &&
          notifications < filters.minNotifications
        ) {
          return false;
        }
        if (
          filters.maxNotifications !== null &&
          notifications > filters.maxNotifications
        ) {
          return false;
        }

        return true;
      }),
    [attendees, filters],
  );

  const handleSelectAll = () => {
    const newSelected = new Set(selectedIds);
    filteredAttendees.forEach((attendee) => {
      if (attendee.guid) {
        newSelected.add(attendee.guid);
      }
    });
    setSelectedIds(newSelected);
  };

  const handleDeselectAll = () => {
    const filteredGuids = new Set(
      filteredAttendees.map((a) => a.guid).filter(Boolean) as string[],
    );
    const newSelected = new Set(
      Array.from(selectedIds).filter((id) => !filteredGuids.has(id)),
    );
    setSelectedIds(newSelected);
  };

  const handleToggleAttendee = (guid: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(guid)) {
      newSelected.delete(guid);
    } else {
      newSelected.add(guid);
    }
    setSelectedIds(newSelected);
  };

  const handleNext = () => {
    const selected = attendees.filter(
      (attendee) => attendee.guid && selectedIds.has(attendee.guid),
    );
    onNext(selected);
  };

  const setMinConnections = (value: string | null) => {
    let parsedValue = value ? Number(value) : null;
    if (parsedValue === null || parsedValue < 1) {
      parsedValue = null;
    }
    setFilters({
      ...filters,
      minConnections: parsedValue,
    });
  };

  const setMaxConnections = (value: string | null) => {
    let parsedValue = value ? Number(value) : null;
    if (parsedValue === null || parsedValue < 1) {
      parsedValue = null;
    }
    setFilters({
      ...filters,
      maxConnections: parsedValue,
    });
  };

  const setMinNotifications = (value: string | null) => {
    let parsedValue = value ? Number(value) : null;
    if (parsedValue === null || parsedValue < 1) {
      parsedValue = null;
    }
    setFilters({
      ...filters,
      minNotifications: parsedValue,
    });
  };

  const setMaxNotifications = (value: string | null) => {
    let parsedValue = value ? Number(value) : null;
    if (parsedValue === null || parsedValue < 1) {
      parsedValue = null;
    }
    setFilters({
      ...filters,
      maxNotifications: parsedValue,
    });
  };

  return (
    <div className="flex flex-col h-full w-full bg-black text-white/90 text-left">
      {/* Header */}
      <div className="px-6 py-4">
        <h2 className="text-lg font-semibold">Select Recipients</h2>
      </div>

      {/* Filters */}
      <div className="px-6 space-y-4 mb-4">
        {/* Search */}
        <GuestSearchBar
          value={filters.searchQuery}
          onChange={(value) => setFilters({ ...filters, searchQuery: value })}
          placeholder="search..."
        />

        {/* Status Filter */}
        <GuestFilterTabs
          activeFilter={filters.status}
          onFilterChange={(newFilter: EventAttendeeStatus) => {
            if (newFilter === filters.status) {
              setFilters({
                ...filters,
                status: null,
              });
            } else {
              setFilters({
                ...filters,
                status: newFilter,
              });
            }
          }}
          counts={counts}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-0">
          {/* Connection Filters */}
          <div className="flex flex-row items-center space-x-4  justify-center">
            <p className="block text-sm font-medium">connections (min/max)</p>
            <div className="flex flex-row gap-1 items-center">
              <PInput
                type="number"
                min="0"
                className="w-16"
                inputClassName="text-center"
                value={filters.minConnections || ""}
                onChange={(e) => setMinConnections(e.target.value)}
              />
              <p>-</p>
              <PInput
                type="number"
                min="0"
                className="w-16"
                inputClassName="text-center"
                value={filters.maxConnections || ""}
                onChange={(e) => setMaxConnections(e.target.value)}
              />
            </div>
          </div>

          {/* Notification Filters */}
          <div className="flex flex-row items-center space-x-4 justify-center">
            <p className="block text-sm font-medium">notifications (min/max)</p>
            <div className="flex flex-row gap-1 items-center">
              <PInput
                type="number"
                min="0"
                className="w-16"
                inputClassName="text-center"
                value={filters.minNotifications || ""}
                onChange={(e) => setMinNotifications(e.target.value)}
              />
              <p>-</p>
              <PInput
                type="number"
                min="0"
                className="w-16"
                inputClassName="text-center"
                value={filters.maxNotifications || ""}
                onChange={(e) => setMaxNotifications(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Selection Controls */}
      <div className="px-6 py-3 border-t border-b bg-white/20 flex items-center justify-between">
        <div className="text-sm">
          {filteredAttendees.length} attendees shown, {selectedIds.size}{" "}
          selected
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSelectAll}
            className="text-sm text-cyan-400 hover:text-cyan-500 font-medium"
          >
            Select all visible
          </button>
          <span className="text-gray-400">|</span>
          <button
            type="button"
            onClick={handleDeselectAll}
            className="text-sm text-cyan-400 hover:text-cyan-500 font-medium"
          >
            Deselect all visible
          </button>
        </div>
      </div>

      {/* Attendee List */}
      <div className="overflow-y-auto max-h-[40vh] sm:max-h-[50vh]">
        {filteredAttendees.map((attendee) => {
          const isSelected = attendee.guid
            ? selectedIds.has(attendee.guid)
            : false;

          return (
            <button
              type="button"
              onClick={() => handleToggleAttendee(attendee.guid || "")}
              aria-label="Toggle attendee selection"
              className="w-full"
            >
              <div
                key={attendee.guid}
                className="flex items-center gap-3  hover:bg-white/20 transition-colors cursor-pointer px-3"
              >
                <PCheckbox
                  checked={isSelected}
                  onChange={() =>
                    attendee.guid && handleToggleAttendee(attendee.guid)
                  }
                />
                <GuestListItem guest={attendee} className="flex-1" />
              </div>
            </button>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-white/20">
        <PButton
          type="button"
          kind="primary"
          onClick={handleNext}
          disabled={selectedIds.size === 0}
          className="w-full"
        >
          Next ({selectedIds.size} selected)
        </PButton>
      </div>
    </div>
  );
};

SelectAttendeesScreen.defaultProps = {
  selectedAttendees: null,
};

export default SelectAttendeesScreen;
